

import ErrorBoundary from '../ErrorBoundary';
import { User, Star, Calendar, Shield } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface HostInfoProps {
  hostName?: string;
  isHomeCharger: boolean;
  hostSince?: string;
  hostRating?: number;
}

/**
 * Component that displays information about the charging station host
 */
const HostInfo = ({ 
  hostName, 
  isHomeCharger,
  hostSince = '2023',
  hostRating
}: HostInfoProps) => {
  // If not a home charger or no host name is provided, don't render anything
  if (!isHomeCharger) return null;

  const displayName = hostName || 'Anonymous Host';
  const validRating = typeof hostRating === 'number' && !isNaN(hostRating) ? hostRating : undefined;

  return (
    <ErrorBoundary>
      <Card className="overflow-hidden transition-all duration-300 hover:shadow-md">
        <CardContent className="p-5">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Shield className="h-5 w-5 mr-2 text-blue-500" />
            About the host
          </h2>
          
          <div className="flex items-center">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-xl font-medium mr-4 text-white">
              {displayName ? (
                <span>{displayName.charAt(0).toUpperCase()}</span>
              ) : (
                <User className="h-6 w-6" />
              )}
            </div>
            
            <div>
              <h3 className="font-medium text-lg">{displayName}</h3>
              <div className="flex items-center text-sm text-muted-foreground">
                <Calendar className="h-3.5 w-3.5 mr-1.5" />
                <span>Joined in {hostSince}</span>
              </div>
              
              {validRating !== undefined && (
                <div className="flex items-center mt-2">
                  <Star className="h-4 w-4 text-yellow-500 mr-1" />
                  <span className="font-medium">{validRating.toFixed(1)}</span>
                  <span className="text-muted-foreground text-sm ml-1">rating</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-4 pt-3 border-t">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="bg-blue-50">Verified</Badge>
              <Badge variant="outline" className="bg-green-50">Quick Responder</Badge>
              {validRating && validRating >= 4.5 && (
                <Badge variant="outline" className="bg-yellow-50">Top Rated</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </ErrorBoundary>
  );
};

export default HostInfo;
